<template>
	<component
		:is="as"
		:type="as === 'button' ? type : undefined"
		:class="buttonClasses"
		:disabled="as === 'button' ? disabled || loading : undefined"
		:role="as !== 'button' ? 'button' : undefined"
		:aria-disabled="as !== 'button' && (disabled || loading) ? true : undefined"
		:tabindex="as !== 'button' && (disabled || loading) ? -1 : undefined"
		v-bind="filteredAttrs"
		@click="handleClick"
	>
		<AppSpinner v-if="loading && !loadingText" class="absolute" />
		<span v-if="icon" class="ht-icon" :class="iconClasses">{{ icon }}</span>
		<span v-if="hasContent" :class="contentClasses">
			<template v-if="loading && loadingText">{{ loadingText }}</template>
			<slot v-else>{{ text }}</slot>
		</span>
		<span v-if="iconRight" class="ht-icon" :class="iconClasses">{{
			iconRight
		}}</span>
	</component>
</template>

<script setup lang="ts">
import { computed, useAttrs, useSlots } from "vue";
import { twMerge } from "tailwind-merge";
import AppSpinner from "./AppSpinner.vue";

type Variant =
	| "primary"
	| "secondary"
	| "danger"
	| "success"
	| "ghost"
	| "link";
type Size = "default" | "sm" | "lg";

interface Props {
	as?: string | object;
	variant?: Variant;
	size?: Size;
	loading?: boolean;
	loadingText?: string;
	text?: string;
	icon?: string;
	iconRight?: string;
	disabled?: boolean;
	type?: "button" | "submit" | "reset";
}

const props = withDefaults(defineProps<Props>(), {
	as: "button",
	variant: "secondary",
	size: "default",
	loading: false,
	disabled: false,
	type: "button",
});

defineOptions({
	inheritAttrs: false,
});

const attrs = useAttrs();
const slots = useSlots();

const isNonInteractive = computed(() => props.disabled || props.loading);
const isButton = computed(() => props.as === "button");

const filteredAttrs = computed(() => {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { onClick: _, class: __, ...rest } = attrs;
	return rest;
});

function handleClick(event: MouseEvent) {
	if (isNonInteractive.value && !isButton.value) {
		event.preventDefault();
		event.stopPropagation();
		return;
	}
	const onClick = attrs.onClick as ((e: MouseEvent) => void) | undefined;
	onClick?.(event);
}

const hasContent = computed(() => {
	return slots.default || props.text || (props.loading && props.loadingText);
});

const isIconOnly = computed(() => {
	return (props.icon || props.iconRight) && !hasContent.value;
});

type VueClassValue =
	| string
	| Record<string, unknown>
	| Array<string | Record<string, unknown>>
	| undefined;

function normalizeClass(value: VueClassValue): string {
	if (value === undefined || value === null) {
		return "";
	}
	if (typeof value === "string") {
		return value;
	}
	if (Array.isArray(value)) {
		return value.map((item) => normalizeClass(item as VueClassValue)).join(" ");
	}
	if (typeof value === "object") {
		return Object.entries(value)
			.filter(([, v]) => v)
			.map(([k]) => k)
			.join(" ");
	}
	return "";
}

const baseClasses =
	"relative inline-flex items-center justify-center font-semibold rounded-sm cursor-pointer";

const buttonOnlyClasses =
	"enabled:cursor-pointer disabled:opacity-60 disabled:cursor-default";

const variantBaseClasses: Record<Variant, string> = {
	primary: "bg-[var(--accent-primary)] text-[var(--text-invert)]",
	secondary:
		"bg-white border border-[var(--border)] text-[var(--text-primary)]",
	danger: "bg-[var(--color-fail)] text-[var(--text-on-danger)]",
	success: "bg-[#c7f1ce] text-[#57a964] border border-[#c7f1ce]",
	ghost: "bg-transparent text-[var(--text-secondary)] font-normal",
	link: "bg-transparent text-[var(--text-link)] underline font-normal",
};

const variantHoverClasses: Record<Variant, { button: string; other: string }> =
	{
		primary: {
			button: "enabled:hover:bg-[var(--accent-primary-hover)]",
			other: "hover:bg-[var(--accent-primary-hover)]",
		},
		secondary: {
			button: "enabled:hover:bg-[var(--background-secondary)]",
			other: "hover:bg-[var(--background-secondary)]",
		},
		danger: {
			button: "enabled:hover:bg-[var(--color-fail-darker)]",
			other: "hover:bg-[var(--color-fail-darker)]",
		},
		success: {
			button: "enabled:hover:brightness-95",
			other: "hover:brightness-95",
		},
		ghost: {
			button: "enabled:hover:bg-gray-100",
			other: "hover:bg-gray-100",
		},
		link: {
			button: "enabled:hover:text-[var(--text-link-hover)]",
			other: "hover:text-[var(--text-link-hover)]",
		},
	};

const sizeClasses: Record<
	Size,
	{ base: string; padding: string; iconOnlyPadding: string }
> = {
	default: {
		base: "text-sm gap-1",
		padding: "px-2.5 py-1.5",
		iconOnlyPadding: "p-1.5",
	},
	sm: { base: "text-xs gap-1", padding: "px-2 py-1", iconOnlyPadding: "p-1" },
	lg: { base: "text-base gap-2", padding: "px-4 py-2", iconOnlyPadding: "p-2" },
};

const nonButtonDisabledClasses = "opacity-60 cursor-default";

const buttonClasses = computed(() => {
	const hoverClasses =
		variantHoverClasses[props.variant][isButton.value ? "button" : "other"];
	const size = sizeClasses[props.size];
	const paddingClass = isIconOnly.value ? size.iconOnlyPadding : size.padding;
	const nonButtonDisabled =
		!isButton.value && isNonInteractive.value ? nonButtonDisabledClasses : "";
	const classes = [
		baseClasses,
		isButton.value ? buttonOnlyClasses : "",
		nonButtonDisabled,
		variantBaseClasses[props.variant],
		isNonInteractive.value && !isButton.value ? "" : hoverClasses,
		size.base,
		props.variant !== "link" ? paddingClass : "",
	];

	return twMerge(
		classes.join(" "),
		normalizeClass(attrs.class as VueClassValue),
	);
});

const iconClasses = computed(() => {
	const sizes: Record<Size, string> = {
		default: "text-xl",
		sm: "text-lg",
		lg: "text-2xl",
	};

	return [
		sizes[props.size],
		props.variant === "secondary" ? "text-[var(--text-secondary)]" : "",
		props.loading && !props.loadingText ? "invisible" : "",
	];
});

const contentClasses = computed(() => {
	return [
		"inline-flex items-center gap-[inherit] whitespace-nowrap",
		props.loading && !props.loadingText ? "invisible" : "",
	];
});
</script>
